import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
    Building2, ShieldCheck, Sparkles, ClipboardList, ClipboardCheck,
    TrendingUp, Coins, Recycle, Eye,
} from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

export default function ClientPortal() {
    const [clients, setClients] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [view, setView] = useState(null);
    const [busy, setBusy] = useState(false);
    const [me, setMe] = useState(null);
    const [brand, setBrand] = useState(null);

    useEffect(() => {
        api.get("/edi/portal/me").then((r) => setMe(r.data)).catch(() => setMe(null));
        api.get("/edi/portal/clients")
            .then((r) => {
                setClients(r.data.clients || []);
                if (r.data.clients?.[0]) setSelectedKey(r.data.clients[0].key);
            })
            .catch(() => toast.error("Failed to load Client Portal"));
    }, []);

    useEffect(() => {
        if (!selectedKey) return;
        setBusy(true);
        Promise.all([
            api.get(`/edi/portal/${selectedKey}`),
            api.get(`/edi/portal/${selectedKey}/brand`).catch(() => ({ data: null })),
        ])
            .then(([viewRes, brandRes]) => {
                setView(viewRes.data);
                setBrand(brandRes.data);
            })
            .catch(() => toast.error("Failed to load client scope"))
            .finally(() => setBusy(false));
    }, [selectedKey]);

    // Fallback palette when no brand is stored — matches SGS Bateman defaults.
    const palette = useMemo(() => ({
        primary:   brand?.primary_color   || "#D4AF37",
        secondary: brand?.secondary_color || "#020617",
        accent:    brand?.accent_color    || "#94A3B8",
        tagline:   brand?.tagline         || "Enterprise Delivery Intelligence Platform™",
        cover:     brand?.cover_gradient  || "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.28), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.20), transparent 55%)",
        mark:      brand?.logo_mark       || "SGS",
        logo:      brand?.logo_url        || null,
        org:       brand?.org_name        || "SGS Bateman",
    }), [brand]);

    return (
        <div className="space-y-8" data-testid={EDI.clientPortalRoot}
             style={{ "--tenant-primary": palette.primary,
                        "--tenant-secondary": palette.secondary,
                        "--tenant-accent": palette.accent }}>
            {/* Tenant identity hero — white-label brand ring */}
            <div className="relative overflow-hidden rounded-2xl glass p-8">
                <div className="absolute inset-0 opacity-40 pointer-events-none"
                     style={{ background: palette.cover }} />
                <div className="relative flex items-start gap-4 flex-wrap justify-between">
                    <div className="flex items-center gap-4">
                        {palette.logo ? (
                            <img src={palette.logo} alt={palette.org}
                                    data-testid="edi-tenant-logo"
                                    className="h-14 w-14 object-contain rounded-md bg-slate-950/50 border border-slate-800" />
                        ) : (
                            <div className="h-14 w-14 rounded-md border-2 grid place-items-center font-display font-black text-lg tracking-tight"
                                    data-testid="edi-tenant-logo-mark"
                                    style={{ borderColor: palette.primary, color: palette.primary }}>
                                {palette.mark}
                            </div>
                        )}
                        <div>
                            <div className="kicker" style={{ color: palette.primary }} data-testid="edi-tenant-kicker">
                                Client Portal™ · {palette.org}
                            </div>
                            <h1 className="mt-1 font-display font-black text-white text-3xl md:text-4xl tracking-tighter" data-testid="edi-tenant-org">
                                {palette.org}
                            </h1>
                            <p className="mt-2 text-slate-300 font-body text-sm max-w-2xl">
                                {palette.tagline}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 font-body text-xs" style={{ color: palette.primary }}>
                        <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Tenant-isolated · read-only · watermarked</span>
                    </div>
                </div>
            </div>

            {/* Client selector — hidden when the logged-in user is locked to a single tenant */}
            {!me?.portal_locked && (
                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-500 text-[10px] mb-3">Executive tenant preview — select a client</div>
                    <div className="flex flex-wrap gap-2">
                        {clients.map((c) => (
                            <button
                                key={c.key}
                                data-testid={EDI.clientPortalSelect(c.key)}
                                onClick={() => setSelectedKey(c.key)}
                                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-display font-bold border transition-colors duration-200 ${
                                    selectedKey === c.key
                                        ? "text-slate-950 border-transparent"
                                        : "border-slate-800 text-slate-300 hover:text-[var(--sgsb-gold)] hover:border-[color:var(--sgsb-gold)]/50"
                                }`}
                                style={selectedKey === c.key ? { background: palette.primary, borderColor: palette.primary } : undefined}
                            >
                                <Building2 className="h-3.5 w-3.5" strokeWidth={2} />
                                {c.client}
                                <span className="text-[10px] opacity-70">{c.projects} projects · {c.reuse_pct}% reuse</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {me?.portal_locked && (
                <div className="rounded-md px-4 py-2 text-xs font-body inline-flex items-center gap-2 border"
                     data-testid="edi-client-portal-locked"
                     style={{ background: `${palette.primary}1A`, borderColor: `${palette.primary}66`, color: palette.primary }}>
                    <ShieldCheck className="h-3 w-3" strokeWidth={2} />
                    Tenant-locked · {me?.name} · scoped to {view?.client || me?.assigned_client}
                </div>
            )}

            {busy && !view && <div className="kicker text-slate-500">Loading client scope…</div>}

            {view && (
                <>
                    {/* Watermark */}
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-300 font-body inline-flex items-center gap-2">
                        <Eye className="h-3 w-3" strokeWidth={2} />
                        {view.watermark}
                    </div>

                    {/* KPIs scoped to this client */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { l: "Reuse Rate",   v: `${view.kpis.reuse_pct}%`,           Icon: TrendingUp },
                            { l: "Hours Saved",  v: view.kpis.hours_saved.toLocaleString(), Icon: Sparkles },
                            { l: "Cost Saved",   v: view.kpis.cost_saved,                Icon: Coins },
                            { l: "Board Packs",  v: view.kpis.board_packs,               Icon: ClipboardCheck },
                        ].map(({ l, v, Icon }) => (
                            <div key={l} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10" style={{ background: palette.primary }} />
                                <Icon className="h-4 w-4" style={{ color: palette.primary }} strokeWidth={1.5} />
                                <div className="mt-2 kicker text-slate-400 text-[10px]">{l}</div>
                                <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                            </div>
                        ))}
                    </div>

                    {/* Projects scoped to this client */}
                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <Building2 className="h-4 w-4" style={{ color: palette.primary }} strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">{view.client} · Projects</h3>
                            <div className="flex-1" />
                            <span className="kicker text-[10px] text-slate-500">{view.projects.length} projects</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-4 py-3">Project</th>
                                        <th className="text-left px-4 py-3">Commodity</th>
                                        <th className="text-left px-4 py-3">Capacity</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-right px-4 py-3">Progress</th>
                                        <th className="text-right px-4 py-3">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {view.projects.map((p) => (
                                        <tr key={p.name} className="border-t border-slate-800/60">
                                            <td className="px-4 py-2 text-slate-100">{p.name}</td>
                                            <td className="px-4 py-2 text-slate-300">{p.commodity}</td>
                                            <td className="px-4 py-2 text-slate-400">{p.capacity}</td>
                                            <td className="px-4 py-2 text-slate-300">{p.status}</td>
                                            <td className="px-4 py-2 text-right">
                                                <div className="inline-flex items-center gap-2">
                                                    <div className="h-1.5 w-24 rounded-full bg-slate-800 overflow-hidden">
                                                        <div className="h-full" style={{ width: `${p.progress_pct}%`, background: palette.primary }} />
                                                    </div>
                                                    <span className="font-display font-bold" style={{ color: palette.primary }}>{p.progress_pct}%</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 text-right text-slate-200 font-display font-bold">${p.value_musd}M</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Board packs scoped to this client */}
                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <ClipboardList className="h-4 w-4" style={{ color: palette.primary }} strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">{view.client} · Board Packs</h3>
                        </div>
                        <div className="divide-y divide-slate-800/60">
                            {view.board_packs.map((b) => (
                                <div key={b.ref} className="px-4 py-3 flex items-center gap-3">
                                    <span className="kicker text-slate-500 text-[10px]">{b.ref}</span>
                                    <div className="flex-1 text-sm text-slate-100">{b.title}</div>
                                    <span className="text-[11px] text-slate-500 font-body">{b.issued}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

